import { Add as AddIcon, FilterList as FilterListIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { Alert, Button, IconButton, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PermissionButton } from '@/components/common/PermissionButton/PermissionButton';
import { TanStackTableViewWithSchema } from '@/components/common/Table';
import ReaderMqttConfigDialog from '@/components/rfid/ReaderMqttConfigDialog';
import { PAGE_SIZE_OPTIONS } from '@/config/constants';
import rfidService, { Reader } from '@/services/rfidService';
import { useToast } from '@/components/common/Toast/ToastProvider';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';
import { hasActiveFilters } from '@/utils/helpers';

const ReaderList: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [readers, setReaders] = useState<Reader[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configReader, setConfigReader] = useState<Reader | null>(null);
  const {
    page, rowsPerPage, searchTerm, sortKey, sortDirection, filters,
    handlePageChange, handleRowsPerPageChange, handleSortChange, handleFilterChange,
    clearFilters, toCreate, toDetail, toEdit,
  } = useCompletePaginationNavigation(navigate, '/rfid/readers');

  const query = useMemo(() => {
    const params: Record<string, unknown> = { page: page + 1, page_size: rowsPerPage, cf: true };
    if (searchTerm) params.search = searchTerm;
    if (sortKey) params.ordering = sortDirection === 'desc' ? `-${sortKey}` : sortKey;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') params[key] = value;
    });
    return params;
  }, [filters, page, rowsPerPage, searchTerm, sortDirection, sortKey]);

  const loadReaders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rfidService.getReaders(query);
      setReaders(response.results ?? []);
      setTotalCount(response.count ?? 0);
    } catch (loadError) {
      console.error('Unable to load RFID readers', loadError);
      setError('Unable to load RFID readers');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { loadReaders(); }, [loadReaders]);

  const deleteReader = async (id: string | number) => {
    try {
      await rfidService.deleteReader(String(id));
      showToast('Reader deleted successfully', 'success');
      loadReaders();
    } catch (deleteError) {
      console.error('Unable to delete reader', deleteError);
      showToast('Unable to delete reader', 'error');
    }
  };

  return (
    <div className="appWrapper">
      <h3 className="pageTitle">RFID Readers</h3>
      <div className="appGridContainer">
        <div className="gridHeader">
          <div className="gridHeadLeft">
            <Button variant="outlined" size="small" startIcon={<FilterListIcon />} onClick={clearFilters} disabled={!hasActiveFilters(filters)}>Clear Filters</Button>
          </div>
          <div className="gridHeadRight">
            <PermissionButton module="rfid_reader" permissionAction="add" fallbackPermission="add_reader_scr" variant="contained" startIcon={<AddIcon />} onClick={toCreate}>Add Reader</PermissionButton>
          </div>
        </div>
        <div className="gridWrapper">
          {error ? <Alert severity="error">{error}</Alert> : (
            <TanStackTableViewWithSchema
              data={readers}
              loading={loading}
              rowKey={(reader) => reader.id}
              entity="rfid_reader"
              enableAPIConfig
              permissionModule="reader"
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              onFilterChange={handleFilterChange}
              filterValues={filters}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onRowClick={(reader) => toDetail(reader.id)}
              handleView={toDetail}
              handleEdit={toEdit}
              handleDelete={deleteReader}
              confirmDelete={{ title: 'Delete RFID reader?', message: 'The reader will be archived. Its name and device ID can be used again.' }}
              fallbackHeaders={[]}
              renderRowActions={(reader) => (
                <Tooltip title="View Configuration">
                  <IconButton size="small" aria-label={`View configuration for ${reader.name}`} onClick={(event) => { event.stopPropagation(); setConfigReader(reader); }}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            />
          )}
        </div>
      </div>
      <ReaderMqttConfigDialog open={Boolean(configReader)} reader={configReader} onClose={() => setConfigReader(null)} />
    </div>
  );
};

export default ReaderList;
